import React, { useState, useRef, useEffect } from "react";
import {
  Settings,
  X,
  Check,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  FileText, // Added FileText icon
} from "lucide-react";
import { StackedProgressBar } from "./StackedProgressBar";

export const DataTable = ({
  data = [],
  columns = [],
  sortableKeys = [],
  title = "",
  subtitle = "",
  formatCurrency = (val) => val,
  // Pagination props
  currentPage = 1,
  totalPages = 1,
  onPageChange = () => {},
  onNext = () => {},
  onPrev = () => {},
  onSort = () => {}, // New prop for handling sort
  rightSlot = null, // New prop for custom right content
  showHeader = true,
  className = "",
  minHeight = "400px", // New prop for minimum height
}) => {
  const [visibleColumns, setVisibleColumns] = useState(
    columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
  );
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
  const [isColumnsExpanded, setIsColumnsExpanded] = useState(false);
  const [isSortExpanded, setIsSortExpanded] = useState(false);
  const [activeSortKey, setActiveSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const columnSelectorRef = useRef(null);

  // Handle click outside for column selector
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        columnSelectorRef.current &&
        !columnSelectorRef.current.contains(event.target)
      ) {
        setIsColumnSelectorOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleColumn = (columnKey) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  const handleSort = (columnKey) => {
    if (activeSortKey === columnKey) {
      // Toggle direction if same column
      const newDirection = sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(newDirection);
      onSort(columnKey, newDirection);
    } else {
      // New column, set to asc
      setActiveSortKey(columnKey);
      setSortDirection("asc");
      onSort(columnKey, "asc");
    }
  };

  const visibleColumnsList = columns.filter((col) => visibleColumns[col.key]);

  // Generate pagination numbers
  const getPaginationNumbers = () => {
    const maxVisible = 5;
    const range = [];
    const rangeWithDots = [];

    // Calculate the range based on current page
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + maxVisible - 1);

    // Adjust start if we're near the end
    if (end === totalPages) {
      start = Math.max(1, end - maxVisible + 1);
    }

    // Add first page and dots if needed
    if (start > 1) {
      rangeWithDots.push(1);
      if (start > 2) {
        rangeWithDots.push("...");
      }
    }

    // Add visible page numbers
    for (let i = start; i <= end; i++) {
      rangeWithDots.push(i);
    }

    // Add last page and dots if needed
    if (end < totalPages) {
      if (end < totalPages - 1) {
        rangeWithDots.push("...");
      }
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  return (
    <div 
      className={`orders-container ${className}`}
      style={{ minHeight: minHeight }}
    >
      <div className="orders-wrapper">
        {/* Header Section - Fixed */}
        {showHeader && (
          <div className="orders-header-section">
            <div className="orders-header-content">
              {/* Left side - Title and subtitle */}
              <div className="flex items-center gap-4">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">
                    {title}
                  </h2>
                  {subtitle && (
                    <p className="text-sm text-gray-500">{subtitle}</p>
                  )}
                </div>
              </div>

              {/* Right side - Custom content and settings */}
              <div className="flex items-center gap-3">
                {rightSlot && rightSlot}

                {/* Settings/Column Visibility */}
                <div className="relative" ref={columnSelectorRef}>
                  <button
                    onClick={() =>
                      setIsColumnSelectorOpen(!isColumnSelectorOpen)
                    }
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Settings className="w-5 h-5 text-gray-600" />
                  </button>

                  {isColumnSelectorOpen && (
                    <div className="absolute right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-300 w-64 z-50">
                      <div className="p-3 border-b border-gray-300 flex items-center justify-between">
                        <h3 className="font-medium text-sm">Table Options</h3>
                        <button
                          onClick={() => setIsColumnSelectorOpen(false)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="max-h-[300px] overflow-y-auto">
                        {/* Visible Columns Section */}
                        <div className="border-b border-gray-300">
                          <button
                            onClick={() =>
                              setIsColumnsExpanded(!isColumnsExpanded)
                            }
                            className="w-full p-3 flex items-center justify-between hover:bg-gray-50"
                          >
                            <h4 className="text-sm font-medium text-gray-700">
                              Visible Columns
                            </h4>
                            <ChevronDown
                              className={`w-4 h-4 text-gray-500 transition-transform ${
                                isColumnsExpanded ? "transform rotate-180" : ""
                              }`}
                            />
                          </button>
                          {isColumnsExpanded && (
                            <div className="px-3 pb-3">
                              <div className="space-y-1">
                                {columns.map((column) => (
                                  <label
                                    key={column.key}
                                    className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={visibleColumns[column.key]}
                                      onChange={() => toggleColumn(column.key)}
                                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <span className="text-sm text-gray-700">
                                      {column.label}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Sort By Section */}
                        {sortableKeys.length > 0 && (
                          <div>
                            <button
                              onClick={() => setIsSortExpanded(!isSortExpanded)}
                              className="w-full p-3 flex items-center justify-between hover:bg-gray-50"
                            >
                              <h4 className="text-sm font-medium text-gray-700">
                                Sort By
                              </h4>
                              <ChevronDown
                                className={`w-4 h-4 text-gray-500 transition-transform ${
                                  isSortExpanded ? "transform rotate-180" : ""
                                }`}
                              />
                            </button>
                            {isSortExpanded && (
                              <div className="px-3 pb-3">
                                <div className="space-y-1">
                                  {columns
                                    .filter((col) =>
                                      sortableKeys.includes(col.key)
                                    )
                                    .map((column) => (
                                      <div
                                        key={column.key}
                                        className={`p-2 rounded ${
                                          activeSortKey === column.key
                                            ? "bg-gray-50"
                                            : ""
                                        }`}
                                      >
                                        <div className="flex items-center justify-between">
                                          <span className="text-sm text-gray-700">
                                            {column.label}
                                          </span>
                                          <div className="flex gap-0.5">
                                            <button
                                              onClick={() => {
                                                setActiveSortKey(column.key);
                                                setSortDirection("asc");
                                                onSort(column.key, "asc");
                                              }}
                                              className={`p-0.5 rounded hover:bg-gray-200 ${
                                                activeSortKey === column.key &&
                                                sortDirection === "asc"
                                                  ? "text-indigo-600 bg-indigo-50"
                                                  : "text-gray-400"
                                              }`}
                                            >
                                              <ArrowUp className="w-3 h-3" />
                                            </button>
                                            <button
                                              onClick={() => {
                                                setActiveSortKey(column.key);
                                                setSortDirection("desc");
                                                onSort(column.key, "desc");
                                              }}
                                              className={`p-0.5 rounded hover:bg-gray-200 ${
                                                activeSortKey === column.key &&
                                                sortDirection === "desc"
                                                  ? "text-indigo-600 bg-indigo-50"
                                                  : "text-gray-400"
                                              }`}
                                            >
                                              <ArrowDown className="w-3 h-3" />
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Table Section - Scrollable */}
        <div className="orders-table-section">
          <div className="orders-table-container hide-scrollbar">
            <table className="orders-table">
              <thead className="orders-table-header">
                <tr>
                  {visibleColumnsList.map((column) => (
                    <th
                      key={column.key}
                      className="orders-table-header-cell"
                      onClick={() =>
                        column.sortable !== false && handleSort(column.key)
                      }
                      style={{ width: column.width || '150px' }}
                    >
                      <div
                        className={`flex items-center gap-1 justify-${
                          column.align || "start"
                        }`}
                      >
                        {column.label}
                        {column.sortable !== false &&
                          activeSortKey === column.key &&
                          (sortDirection === "asc" ? (
                            <ArrowUp className="w-4 h-4" />
                          ) : (
                            <ArrowDown className="w-4 h-4" />
                          ))}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="orders-table-body">
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumnsList.length}>
                      <div className="orders-empty-state">
                        <FileText className="orders-empty-icon" />
                        <p className="orders-empty-text">No data available</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  data.map((row, rowIndex) => (
                    <tr key={rowIndex} className="orders-table-row">
                      {visibleColumnsList.map((column) => (
                        <td
                          key={column.key}
                          className="orders-table-cell"
                          style={{ width: column.width || '150px' }}
                        >
                          <div
                            title={
                              column.render
                                ? column.render(row)
                                : row[column.key]
                            }
                          >
                            {column.render
                              ? column.render(row)
                              : row[column.key]}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="orders-pagination">
              <div className="flex items-center justify-between p-4">
                <div className="text-sm text-gray-700">
                  Page <span className="font-medium">{currentPage}</span> of{" "}
                  <span className="font-medium">{totalPages}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={onPrev}
                    disabled={currentPage === 1}
                    className="orders-pagination-button"
                  >
                    Previous
                  </button>

                  <div className="flex space-x-1">
                    {getPaginationNumbers().map((pageNum, index) => (
                      <React.Fragment key={index}>
                        {pageNum === "..." ? (
                          <span className="orders-pagination-button">...</span>
                        ) : (
                          <button
                            onClick={() => onPageChange(pageNum)}
                            className={`orders-pagination-button ${
                              Number(currentPage) === Number(pageNum)
                                ? "orders-pagination-button-active"
                                : ""
                            }`}
                          >
                            {pageNum}
                          </button>
                        )}
                      </React.Fragment>
                    ))}
                  </div>

                  <button
                    onClick={onNext}
                    disabled={currentPage === totalPages}
                    className="orders-pagination-button"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Example usage in Overview.jsx:
/*
const columns = [
  {
    key: 'date',
    label: 'Date',
    render: (row) => new Date(row.date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
    })
  },
  {
    key: 'totalOrders',
    label: 'Orders',
    render: (row) => (
      <span className="table-order-badge">{row.totalOrders}</span>
    )
  },
  {
    key: 'revenue',
    label: 'Revenue',
    render: (row) => formatCurrency(row.salesData.finalAmount)
  },
  {
    key: 'gst',
    label: 'GST Details',
    render: (row) => (
      <div className="table-gst-details">
        <div className="table-gst-row">
          CGST: {formatCurrency(row.taxBreakdown.cgst)}
        </div>
        <div className="table-gst-row">
          SGST: {formatCurrency(row.taxBreakdown.sgst)}
        </div>
        <div className="table-gst-total">
          Total: {formatCurrency(row.taxBreakdown.totalGst)}
        </div>
      </div>
    )
  },
  {
    key: 'paymentDistribution',
    label: 'Payment Distribution',
    width: '320px',
    render: (row) => (
      <div className="w-[240px]">
        <StackedProgressBar
          segments={Object.entries(row.paymentMethods)
            .filter(([_, value]) => value > 0)
            .map(([method, value]) => ({
              label: method.charAt(0).toUpperCase() + method.slice(1),
              value,
              color: paymentColors[method],
              tooltipValue: formatCurrency(value),
            }))}
          total={row.salesData.finalAmount}
        />
      </div>
    )
  }
];

<DataTable
  data={dailyPerformance}
  columns={columns}
  title="Daily Performance"
  subtitle="Day-wise sales breakdown"
  formatCurrency={formatCurrency}
/>
*/
